import {functions, httpsCallable} from "@/firebase/firebaseConfig"

export interface StripeProduct {
    id: string,
    title: string,
    description: string,
    features?: {
        name: string,
        /** Locale-stable key for icon lookup (avoids English-only string matching). */
        iconKey?: string,
        /** Optional accent badge (e.g. Dubbing Plus discount). */
        badge?: string,
    }[],
    metadata?: Record<string, string>,
    prices: {
        id: string,
        amount: number,
        currency: string,
        interval: string,
        description: string,
        // Dedicated per-currency amounts (major units) keyed by uppercase ISO
        // code, e.g. {USD: 12, AUD: 18}. Present when the price has Stripe
        // currency_options configured for the buyer's region.
        currencyOptions?: Record<string, number>,
    }[],
}

export interface EmbeddedCheckoutSession {
    sessionId: string,
    clientSecret: string,
    priceId: string,
    productId: string,
    amount: number,
    currency: string,
    interval: string,
    pppTier: number,
    country?: string,
    hasCurrencyOptions?: boolean,
}

export interface EmbeddedCheckoutSessionRequest {
    uid: string,
    tier: "starter" | "pro" | "business",
    billingCycle: "month" | "year",
    country?: string | null,
    workspaceId?: string | null,
    adminGeoOverride?: string | null,
    toltId?: string,
    coupon?: string,
}

let productsCache: { promise: Promise<StripeProduct[]>; country: string } | null = null
const embeddedSessionCache = new Map<string, Promise<EmbeddedCheckoutSession>>()

function getEmbeddedSessionCacheKey({
    uid,
    tier,
    billingCycle,
    country,
    workspaceId,
    adminGeoOverride,
    coupon,
    toltId,
}: EmbeddedCheckoutSessionRequest): string {
    return [uid, workspaceId || "personal", tier, billingCycle, country || "US", adminGeoOverride || "", coupon || "", toltId || ""].join(":")
}

function requestEmbeddedCheckoutSession(request: EmbeddedCheckoutSessionRequest): Promise<EmbeddedCheckoutSession> {
    const handleCheckout = httpsCallable(functions, 'billing-createEmbeddedCheckoutSession')
    return handleCheckout({
        uid: request.uid,
        tier: request.tier,
        billingCycle: request.billingCycle,
        ...(request.country ? { country: request.country } : {}),
        ...(request.workspaceId ? { workspaceId: request.workspaceId } : {}),
        ...(request.adminGeoOverride ? { adminGeoOverride: request.adminGeoOverride } : {}),
        toltId: request.toltId,
        toltReferral: request.toltId,
        tolt_referral: request.toltId,
        ...(request.coupon ? { coupon: request.coupon } : {}),
    }).then(result => {
        return result.data as EmbeddedCheckoutSession
    }).catch(error => {
        embeddedSessionCache.delete(getEmbeddedSessionCacheKey(request))
        console.error('Error creating embedded checkout session', error)
        throw new Error('Error creating embedded checkout session')
    })
}

export function createCustomerSession(uid: string): Promise<string> {
    const createCustomerSession = httpsCallable(functions, 'billing-createCustomerSession')
    return createCustomerSession({uid}).then(result => {
        return (result.data as { clientSecret: string }).clientSecret
    }).catch(error => {
        console.error('Error creating customer session', error)
        throw new Error('Error creating customer session')
    })
}

export function fetchProducts(country: string = 'US'): Promise<StripeProduct[]> {
    const resolvedCountry = country

    if (productsCache && productsCache.country === resolvedCountry) {
        return productsCache.promise
    }

    const retrieveProducts = httpsCallable(functions, 'billing-retrieveProducts')
    const promise = retrieveProducts({ country: resolvedCountry }).then(result => {
        return result.data as StripeProduct[]
    }).catch(error => {
        productsCache = null
        console.warn('Unable to retrieve billing products', error)
        throw new Error('Error fetching products')
    })

    productsCache = { promise, country: resolvedCountry }
    return promise
}

// NOTE: The `coupon` param requires the `billing-createCheckoutSession` Cloud Function to
// accept and forward it as `discounts: [{ coupon }]` to stripe.checkout.sessions.create().
export function createCheckoutSession(uid: string, mode: string, priceId: string, toltId?: string, coupon?: string): Promise<string> {
    const handleCheckout = httpsCallable(functions, 'billing-createCheckoutSession')
    return handleCheckout({
        uid,
        mode,
        priceId,
        toltId,
        // Send common aliases so backend integrations can pick up either.
        toltReferral: toltId,
        tolt_referral: toltId,
        ...(coupon ? { coupon } : {}),
    }).then(result => {
        const data = result.data as { sessionId?: string; url?: string }
        if (!data.url) {
            throw new Error('Checkout session missing redirect URL')
        }
        return data.url
    }).catch(error => {
        console.error('Error creating checkout session', error)
        throw new Error('Error creating checkout session')
    })
}

export function createEmbeddedCheckoutSession(request: EmbeddedCheckoutSessionRequest): Promise<EmbeddedCheckoutSession> {
    const cacheKey = getEmbeddedSessionCacheKey(request)
    const cached = embeddedSessionCache.get(cacheKey)
    if (cached) {
        return cached
    }

    const promise = requestEmbeddedCheckoutSession(request)
    embeddedSessionCache.set(cacheKey, promise)
    return promise
}

export function prefetchEmbeddedCheckoutSession(request: EmbeddedCheckoutSessionRequest): void {
    void createEmbeddedCheckoutSession(request).catch(() => undefined)
}

export type SavedCardSummary = {
    brand: string
    last4: string
    expMonth: number
    expYear: number
}

export type SavedPaymentMethodQuote = {
    type: string
    oneClickEligible: boolean
    brand?: string
    last4?: string
    email?: string
    label: string
    id?: string
}

export type CreditPurchaseQuote = {
    unitPriceCents: number
    currency?: string
    minCredits: number
    credits: number
    amountCents?: number
    /** @deprecated use amountCents */
    amountUsdCents: number
    balance: number
    billingAccountId: string
    workspaceId?: string | null
    savedPaymentMethod?: SavedPaymentMethodQuote | null
    /** @deprecated use savedPaymentMethod */
    savedCard: SavedCardSummary | null
    /** @deprecated use savedPaymentMethod */
    hasSavedPaymentMethod?: boolean
    /** Checkout client secret for Adaptive Pricing presentment (display only). */
    presentmentClientSecret?: string | null
    presentmentSessionId?: string | null
    presentmentCredits?: number
}

export type CreditPurchasePurpose = "dub_shortfall" | "wallet_topup"
export type CreditPaymentMode = "saved_method" | "new_method" | "saved_card" | "new_card"

export type CreditPurchaseResult =
    | {
        status: "succeeded" | "granted"
        credits: number
        balance: number | null
        paymentIntentId?: string
        purchaseId?: string
    }
    | {
        status: "requires_action"
        clientSecret: string
        paymentIntentId: string
        credits: number
        amountUsdCents: number
        purchaseId?: string
    }
    | {
        status: "requires_elements"
        clientSecret: string
        sessionId: string
        credits: number
        amountUsdCents: number
        purchaseId?: string
        /** Saved card id for Adaptive Pricing one-click confirm (no Payment Element). */
        oneClickPaymentMethodId?: string
    }
    | {
        status: "failed"
        code?: string
        message?: string
        paymentIntentId?: string
        purchaseId?: string
    }
    | {
        status: "pending" | "processing"
        credits: number
        balance: number | null
        purchaseId?: string
        message?: string
    }

export function getCreditPurchaseQuote(params: {
    credits?: number
    workspaceId?: string | null
}): Promise<CreditPurchaseQuote> {
    const callable = httpsCallable(functions, "billing-getCreditPurchaseQuote")
    return callable({
        ...(typeof params.credits === "number" ? {credits: params.credits} : {}),
        ...(params.workspaceId ? {workspaceId: params.workspaceId} : {}),
    }).then((result) => result.data as CreditPurchaseQuote)
}

export function purchaseCredits(params: {
    credits: number
    purchaseId: string
    purpose: CreditPurchasePurpose
    paymentMode: CreditPaymentMode
    dubbingRequestId?: string | null
    workspaceId?: string | null
    watermarkRemovalUserShortsId?: string | null
    watermarkRemovalOutputId?: string | null
}): Promise<CreditPurchaseResult> {
    const callable = httpsCallable(functions, "billing-purchaseCredits")
    return callable({
        credits: params.credits,
        purchaseId: params.purchaseId,
        purpose: params.purpose,
        paymentMode: params.paymentMode === "saved_card" ? "saved_method" :
            params.paymentMode === "new_card" ? "new_method" :
            params.paymentMode,
        ...(params.dubbingRequestId ? {dubbingRequestId: params.dubbingRequestId} : {}),
        ...(params.workspaceId ? {workspaceId: params.workspaceId} : {}),
        ...(params.watermarkRemovalUserShortsId
            ? {watermarkRemovalUserShortsId: params.watermarkRemovalUserShortsId}
            : {}),
        ...(params.watermarkRemovalOutputId
            ? {watermarkRemovalOutputId: params.watermarkRemovalOutputId}
            : {}),
    }).then((result) => result.data as CreditPurchaseResult)
}

export function confirmCreditPurchase(params: {
    purchaseId?: string
    paymentIntentId?: string
    sessionId?: string
} | string): Promise<CreditPurchaseResult> {
    const callable = httpsCallable(functions, "billing-confirmCreditPurchase")
    const payload = typeof params === "string" ?
        {paymentIntentId: params} :
        params
    return callable(payload).then((result) => result.data as CreditPurchaseResult)
}

export async function waitForCreditPurchaseGrant(params: {
    purchaseId: string
    paymentIntentId?: string
    sessionId?: string
}): Promise<CreditPurchaseResult> {
    let last: CreditPurchaseResult = {status: "pending", credits: 0, balance: null, purchaseId: params.purchaseId}
    for (let attempt = 0; attempt < 40; attempt++) {
        last = await confirmCreditPurchase(params)
        if (last.status === "granted" || last.status === "succeeded" || last.status === "failed") {
            return last
        }
        await new Promise((resolve) => setTimeout(resolve, 1500))
    }
    return last
}

/**
 * Creates a Checkout Session (mode=setup, ui_mode=elements) for in-modal
 * On Demand card capture. Returns the session client secret for
 * StripeElementsCheckout (variant="setup").
 */
const setupIntentInFlight = new Map<string, Promise<{
    setupIntentId: string
    clientSecret: string
    sessionId: string
}>>()

export function createSetupIntent(workspaceId?: string | null): Promise<{
    setupIntentId: string
    clientSecret: string
    sessionId: string
}> {
    const cacheKey = workspaceId || "personal"
    const inFlight = setupIntentInFlight.get(cacheKey)
    if (inFlight) {
        return inFlight
    }
    const callable = httpsCallable(functions, "billing-createSetupIntent")
    const promise = callable(workspaceId ? {workspaceId} : {}).then((result) => {
        const data = result.data as {
            setupIntentId?: string
            clientSecret?: string
            sessionId?: string
        }
        if (!data.clientSecret) {
            throw new Error("Setup checkout session missing client secret")
        }
        const sessionId = data.sessionId || data.setupIntentId || ""
        if (!sessionId) {
            throw new Error("Setup checkout session missing id")
        }
        return {
            setupIntentId: sessionId,
            sessionId,
            clientSecret: data.clientSecret,
        }
    }).catch((error) => {
        console.error("Error creating setup checkout session", error)
        throw new Error("Error creating setup session")
    }).finally(() => {
        setupIntentInFlight.delete(cacheKey)
    })
    setupIntentInFlight.set(cacheKey, promise)
    return promise
}

export function createCustomerPortalSession(): Promise<string> {
    const createPortalSession = httpsCallable(functions, 'billing-createCustomerPortalSession')
    return createPortalSession({}).then(result => {
        return (result.data as { url: string }).url
    }).catch(error => {
        console.error('Error creating customer portal session', error)
        throw new Error('Error creating customer portal session')
    })
}